import { RelationshipType } from './entity-type.model'

/**
 * The single type every hierarchical path out of `slug` ends at, or null when
 * there isn't one.
 *
 * `EntityTypeNode.parentSlug` holds one parent because the tree view needs one,
 * but a type can genuinely declare several: a failure mode hangs off a machine
 * *or* a work point, a base condition off a machine *or* a machine technology.
 * Naming either as "the" parent is arbitrary — which one wins depends on the
 * order the relationship types come back — so anything describing a type's
 * place in the hierarchy has to walk all of them.
 *
 * The root usually survives that ambiguity even when the immediate parent does
 * not: both of a failure mode's parents lead to `site`. Where the paths
 * genuinely disagree (`attachment` hangs off almost everything, across trees)
 * this returns null rather than pick one.
 *
 * Returns null for a type that is its own root, so callers can treat "nothing
 * above this" as a single case.
 */
export function rootTypeSlug(relationships: RelationshipType[], slug: string): string | null {
  const parents = hierarchicalParents(relationships)
  const roots = new Set<string>()

  const walk = (current: string, seen: Set<string>) => {
    if (seen.has(current)) return // A cyclical model would otherwise hang.
    const next = parents.get(current)
    if (!next || next.size === 0) {
      roots.add(current)
      return
    }
    for (const parent of next) walk(parent, new Set(seen).add(current))
  }

  walk(slug, new Set())
  if (roots.size !== 1) return null
  const root = [...roots][0]
  return root === slug ? null : root
}

/** target slug -> every source slug declared above it, not just the last. */
function hierarchicalParents(relationships: RelationshipType[]): Map<string, Set<string>> {
  const parents = new Map<string, Set<string>>()
  for (const rel of relationships) {
    if (!rel.is_hierarchical) continue
    const set = parents.get(rel.target_type_slug) ?? new Set<string>()
    set.add(rel.source_type_slug)
    parents.set(rel.target_type_slug, set)
  }
  return parents
}
